/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges, OnInit, QueryList,
  ViewChild, ViewChildren
} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {CrmIssue, CompoApiService, ApiResponse, IssueWebLink, BaseComponent} from 'compob2b-libs';
import {map, takeUntil, tap} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {NbPopoverDirective} from '@nebular/theme';

@Component({
    selector: 'app-web-links-issue',
    templateUrl: './web-links-issue.component.html',
    styleUrls: ['./web-links-issue.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WebLinksIssueComponent extends BaseComponent implements OnInit, OnChanges {

  @Input() issue: CrmIssue;
  @Input() showForm: boolean;
  showComponent = false;
  links: IssueWebLink[] = [];
  loading = false;

  @ViewChildren(NbPopoverDirective) popovers: QueryList<NbPopoverDirective>;

  private linkInput: ElementRef;
  @ViewChild('linkInput', {static: false}) set content(content: ElementRef) {
    if (content) {
      this.linkInput = content;
    }
  }

  form = new UntypedFormGroup({
    crmIssueId: new UntypedFormControl('', Validators.required),
    url: new UntypedFormControl('', Validators.required),
    header: new UntypedFormControl('')
  });

  constructor(private readonly apiService: CompoApiService) {
    super();
  }

  ngOnInit(): void {
    this.loading = true;
    this.form.get('crmIssueId').patchValue(this.issue.id + '');

    this.getWebLinks().pipe(
      tap((response: IssueWebLink[]) => this.handleWebLinkResponse(response)),
      takeUntil(this.unsubscribe)
    ).subscribe();
  }

  ngOnChanges(): void {
    if (this.showForm) {
      this.openForm();
    }
  }

  closeForm() {
    this.showForm = false;
    this.showComponent = this.links.length > 0;
    this.cdr.detectChanges();
  }

  openForm() {
    this.showComponent = true;
    this.showForm = true;
    this.cdr.detectChanges();
    this.linkInput.nativeElement.focus();
  }

  onSubmit() {
    this.loading = true;
    this.apiService.post<IssueWebLink, IssueWebLink>('/issue/web-link', this.form.value).
    pipe(
      map((response: ApiResponse<IssueWebLink>) => response.data),
      tap((response: IssueWebLink) => this.handleSubmitResponse(response)),
      takeUntil(this.unsubscribe)
    ).subscribe();
  }

  private handleSubmitResponse(response: IssueWebLink) {
    this.links.push(response);
    this.loading = false;
    this.form.get('url').patchValue('');
    this.form.get('header').patchValue('');
    this.cdr.detectChanges();
  }

  private getWebLinks(): Observable<IssueWebLink[]> {
    return this.apiService.get<IssueWebLink[]>('/issue/' + this.issue.id + '/web-link').pipe(
      map((response: ApiResponse<IssueWebLink[]>) => response.data)
    );
  }

  private handleWebLinkResponse(response: IssueWebLink[]): void {
    this.links = response;
    this.loading = false;
    if (response.length) {
      this.showComponent = true;
    }

    this.cdr.detectChanges();
  }

  deleteLink(link: IssueWebLink) {
    this.loading = true;
    this.delete(link.id).pipe(
      tap(_ => this.handleDeleteResponse(link.id)),
      takeUntil(this.unsubscribe)
    ).subscribe();
  }

  private handleDeleteResponse(id: number) {
    const index = this.links.findIndex(item => item.id === id);
    if (index !== -1) {
      this.links.splice(index, 1);
      this.loading = false;
    }
    this.cdr.detectChanges();
  }

  private delete(id: number): Observable<string> {
    return this.apiService.delete<string>('/issue/web-link/' + id).
    pipe(map((response: ApiResponse<string>) => response.data));
  }

  closeConfirm() {
    this.popovers.forEach(pop => {
      pop.hide();
    });
  }
}
